import { Box, Button, Divider, Grid, Typography } from '@mui/material'
import axios from 'axios'
import jwtDecode from 'jwt-decode'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { ExpandMore } from '@material-ui/icons'
import ContentWrapper from '../../../components/Layout/ContentWrapper'
import { EPermissionsKeys } from '../../../components/Layout/permissionEnums'
import New_Staffing_Request_Modal from '../components/New_Staffing_Request_Modal/New_Staffing_Request_Modal'
import StaffingRequestCard from '../components/StaffingRequestCard/StaffingRequestCard'
import StaffingRequestStatusStats from '../components/StaffingRequestStatusStats/StaffingRequestStatusStats'

const SECTIONS = {
  pending: { key: 'pending', label: 'Në pritje (Workforce)', statuses: ['pending_workforce'] },
  overdue: { key: 'overdue', label: 'Afati ka skaduar', statuses: ['overdue'] },
  active: { key: 'active', label: 'Aktive (në rekrutim)', statuses: ['approved'] },
  closed: { key: 'closed', label: 'Të mbyllura', statuses: ['completed', 'rejected'] }
}
const SECTION_KEYS = Object.keys(SECTIONS)
const getVisibleSectionKeys = (scope) => ['active', 'overdue'].includes(scope) ? [scope] : SECTION_KEYS

const locations = ['Prishtinë HQ', 'Ferizaj', 'Vushtrri', 'Prizren', 'HO-Prishtinë', 'HO-Ferizaj', 'HO-Vushtrri', 'HO-Prizren']
const requestTypes = ['Backfill', 'Ramp-Up']
const limitCount = 8

const Staffing_Request = () => {
  const token = localStorage.getItem('token')
  const decodedToken = token ? jwtDecode(token) : null
  const userLevel = decodedToken?.user_level
  const permissionKeys = decodedToken?.permissions_keys || []
  const hasAccessToAdd = [1, 2].includes(userLevel)
  const hasAccessToRecruit = [1, 5, 6].includes(userLevel)
  const hasWorkforceAccess = [1, 8].includes(userLevel)
  const hasAccessToApprove = hasWorkforceAccess || permissionKeys.includes(EPermissionsKeys.ALLOWED_TO_APPROVE_STAFFING_REQUEST)
  const hasAccessToEdit = [1, 5, 6, 8].includes(userLevel) || permissionKeys.includes(EPermissionsKeys.ALLOWED_TO_EDIT_STAFFING_REQUEST)

  const [showModalForm, setShowModalForm] = useState(false)
  const [dashboardStats, setDashboardStats] = useState({})
  const [chartScope, setChartScope] = useState('total')
  const [statsLoading, setStatsLoading] = useState(false)

  const [staffingRequests, setStaffingRequests] = useState({
    pending: [],
    active: [],
    overdue: [],
    closed: []
  })

  const [fieldsData, setFieldsData] = useState({
    department: [],
    project: [],
    language: [],
    role: []
  })

  const [limit, setLimit] = useState({
    pendingLimit: limitCount,
    pendingCount: 0,
    activeLimit: limitCount,
    activeCount: 0,
    overdueLimit: limitCount,
    overdueCount: 0,
    closedLimit: limitCount,
    closedCount: 0,
  })

  const [filterOptions, setFilterOptions] = useState({
    project: '',
    department: '',
    location: [],
    language: '',
    request_type: ''
  })

  const buildFilterParams = () => {
    const { project, department, location, language, request_type } = filterOptions
    return {
      ...(department ? { department } : {}),
      ...(project && !department ? { project } : {}),
      ...(location?.length ? { location } : {}),
      ...(language ? { language } : {}),
      ...(request_type ? { request_type } : {})
    }
  }

  const getLanguages = async () => {
    const res = await axios.get('languages')
    if (res.data.success) {
      setFieldsData((prev) => ({ ...prev, language: res.data.data }))
    } else {
      toast.error('Ndodhi një gabim tek gjuhët!')
    }
  }

  const getProjects = async () => {
    try {
      const res = await axios.get('/projects')
      if (res.data.success) {
        setFieldsData((prev) => ({ ...prev, project: res.data.data }))
      }
    } catch (error) {
      console.error(error)
      toast.error('Ndodhi nje gabim tek projektet!')
    }
  }

  const getRoles = async () => {
    try {
      const res = await axios.get('roles')
      if (res.data.success) {
        setFieldsData((prev) => ({ ...prev, role: res.data.data }))
      } else {
        toast.error('Ndodhi nje gabim tek pozicionet!')
      }
    } catch (error) {
      toast.error('Ndodhi nje gabim tek pozicionet!')
    }
  }

  const getStaffingDashboardStats = async (signal) => {
    setStatsLoading(true)
    try {
      const response = await axios.get('/staffingRequest/stats', {
        params: { ...buildFilterParams(), chart_scope: chartScope }
      })

      if (!signal?.cancelled && response.data?.success) {
        setDashboardStats(response.data.data)
      }
    } catch (error) {
      if (signal?.cancelled) return
      console.error('Failed to fetch staffing dashboard stats:', error)
      toast.error('Error fetching dashboard stats')
    } finally {
      if (!signal?.cancelled) setStatsLoading(false)
    }
  }

  const getStaffingRequests = async (sectionKey, currentLimit) => {
    const section = SECTIONS[sectionKey]
    if (!section) return

    try {
      const response = await axios.get('/staffingRequest', {
        params: {
          ...buildFilterParams(),
          status: section.statuses.join(','),
          limit: currentLimit
        }
      })

      if (response.data && response.data.data) {
        setStaffingRequests((prev) => ({ ...prev, [sectionKey]: response.data.data }))
        setLimit((prev) => ({ ...prev, [`${sectionKey}Count`]: response.data.totalCount }))
      }
    } catch (error) {
      console.error('Failed to fetch staffing requests:', error)
      toast.error('Error fetching data')
    }
  }

  const refreshSections = async (sectionKeys) => {
    for (const key of sectionKeys) {
      const limitKey = `${key}Limit`
      // eslint-disable-next-line no-await-in-loop
      await getStaffingRequests(key, limit[limitKey])
    }
    await getStaffingDashboardStats()
  }

  const refreshAfterAction = async (action) => {
    const map = {
      approve: ['pending', 'active'],
      reject: ['pending', 'closed'],
      recruit: ['active', 'closed'],
      edit_pending: ['pending'],
      edit_overdue: ['overdue', 'active', 'pending', 'closed'],
      edit_due_date: ['overdue', 'active', 'pending'],
      delete: ['overdue']
    }
    await refreshSections(map[action] || ['pending', 'active', 'overdue', 'closed'])
  }

  const handleFilterOptions = (key, value) => {
    setFilterOptions((prev) => ({ ...prev, [key]: value }))
  }

  const handleProjectChange = async (projectId) => {
    setFilterOptions((prev) => ({ ...prev, project: projectId, department: '' }))

    if (!projectId) {
      setFieldsData((prev) => ({ ...prev, department: [] }))
      return
    }

    try {
      const res = await axios.get('/departments', {
        params: { projectId }
      })
      if (res.data.success) {
        setFieldsData((prev) => ({ ...prev, department: res.data.data }))
      }
    } catch (error) {
      console.error('Error fetching departments:', error)
      toast.error('Failed to fetch departments')
    }
  }

  useEffect(() => {
    getProjects()
    getLanguages()
    if (hasAccessToAdd) getRoles()
  }, [])

  useEffect(() => {
    const signal = { cancelled: false }
    getStaffingDashboardStats(signal)
    return () => { signal.cancelled = true }
  }, [filterOptions, chartScope])

  useEffect(() => {
    setLimit({
      pendingLimit: limitCount,
      pendingCount: 0,
      activeLimit: limitCount,
      activeCount: 0,
      overdueLimit: limitCount,
      overdueCount: 0,
      closedLimit: limitCount,
      closedCount: 0
    })

    const fetchVisibleSections = async () => {
      const sectionKeys = getVisibleSectionKeys(chartScope)
      await Promise.all(sectionKeys.map((sectionKey) => getStaffingRequests(sectionKey, limitCount)))
    }
    fetchVisibleSections()
  }, [filterOptions, chartScope])

  const renderSection = (sectionKey) => {
    const section = SECTIONS[sectionKey]
    const items = staffingRequests[sectionKey] || []
    const currentLimit = limit[`${sectionKey}Limit`]
    const currentCount = limit[`${sectionKey}Count`]

    if (items.length === 0) {
      return (
        <Typography variant='h6' sx={{ py: 2, textAlign: 'center' }}>
          {`Nuk ka të dhëna për "${section.label}"`}
        </Typography>
      )
    }

    return (
      <Box sx={{ marginTop: '20px' }}>
        <Typography variant='h6' sx={{ py: 2 }}>
          {section.label}
        </Typography>
        <Grid container spacing={2}>
          {items.map((item, index) => (
            <React.Fragment key={`${index}-${item._id}`}>
              <StaffingRequestCard
                item={item}
                canCompleteRequest={userLevel === 1 || (userLevel === 2 && String(item.created_by?._id || item.created_by) === String(decodedToken?.user_id))}
                hasAccessToRecruit={hasAccessToRecruit}
                hasWorkforceAccess={hasWorkforceAccess}
                hasAccessToApprove={hasAccessToApprove}
                hasAccessToEdit={userLevel !== 2 && hasAccessToEdit}
                hasAccessToEditRequested={userLevel === 2
                  ? String(item.created_by?._id || item.created_by) === String(decodedToken?.user_id)
                  : hasAccessToEdit}
                refreshAfterAction={refreshAfterAction}
              />
            </React.Fragment>
          ))}
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <Button
            onClick={() => {
              const newLimit = currentLimit + limitCount
              setLimit((prev) => ({ ...prev, [`${sectionKey}Limit`]: newLimit }))
              getStaffingRequests(sectionKey, newLimit)
            }}
            variant='outlined'
            endIcon={<ExpandMore />}
            disabled={currentCount <= currentLimit}
            sx={{
              minWidth: 132,
              height: 40,
              borderColor: '#d7deea',
              borderRadius: '8px',
              color: '#315fdd',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#fff',
              '&:hover': { borderColor: '#b7c4dc', backgroundColor: '#f8faff' }
            }}
          >
            Më shumë
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <ContentWrapper>

      <StaffingRequestStatusStats
        availableProjects={fieldsData.project}
        availableDepartments={fieldsData.department}
        availableLanguages={fieldsData.language}
        availableLocations={locations}
        availableRequestTypes={requestTypes}
        dashboardStats={dashboardStats}
        chartScope={chartScope}
        onChartScopeChange={setChartScope}
        statsLoading={statsLoading}
        filterOptions={filterOptions}
        onFilterChange={handleFilterOptions}
        onProjectChange={handleProjectChange}
        onAddNew={() => setShowModalForm(true)}
        showAddButton={hasAccessToAdd}
      />

      <Box sx={{ minHeight: '70vh' }}>
        {getVisibleSectionKeys(chartScope).map((sectionKey, index, sectionKeys) => (
          <React.Fragment key={sectionKey}>
            {renderSection(sectionKey)}
            {index < sectionKeys.length - 1 && (
              <Divider sx={{ my: 4, borderColor: '#929292;', color: '#929292;', opacity: '0.2' }} />
            )}
          </React.Fragment>
        ))}
      </Box>

      {showModalForm && (
        <New_Staffing_Request_Modal
          getStaffingRequests={() => refreshAfterAction()}
          closeModal={() => setShowModalForm(false)}
          languages={fieldsData.language}
          roles={fieldsData.role}
          projects={fieldsData.project}
          userLevel={userLevel}
        />
      )}
    </ContentWrapper>
  )
}

export default Staffing_Request
